import { Phone, ArrowRight, Check, X, Shield, AlertTriangle, Heart, Activity, Stethoscope, TestTube, ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const WHATSAPP_LINK = "https://wa.me/5541988451399?text=Olá,%20gostaria%20de%20agendar%20uma%20avaliação%20médica%20para%20reposição%20hormonal.";

export const ReposicaoHormonalPage = () => {
  return (
    <main data-testid="reposicao-hormonal-page" className="pt-24">
      {/* Hero */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-[0.2em] text-[#B8944D] mb-6">
              Dr. Eduardo Hadas — CRM-PR 40929
            </p>
            
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Hormônios não são estética.<br />
              <span className="text-[#B8944D]">São medicina.</span>
            </h1>
            
            <p className="text-lg text-[#666666] leading-relaxed mb-6 max-w-2xl">
              Reposição hormonal conduzida com critério científico, segurança e acompanhamento contínuo, 
              sempre dentro da faixa fisiológica.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hormonal-hero-cta"
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Agendar avaliação médica
              </a>
              <Link
                to="/como-funciona"
                className="btn-secondary flex items-center justify-center gap-2"
              >
                Entenda como funciona
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quando hormônios viram moda */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-6 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#B8944D]/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-7 h-7 text-[#B8944D]" />
              </div>
              <h2 
                className="section-title"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Quando hormônios viram moda, o risco vira rotina.
              </h2>
            </div>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                A reposição hormonal se tornou popular. E, com isso, também se tornou banalizada.
              </p>
              
              <p>
                Hoje, muitas pessoas recebem hormônios sem indicação real, sem exames adequados 
                e sem acompanhamento contínuo.
              </p>
              
              <p>
                O resultado disso não aparece em 30 dias. Aparece meses ou anos depois.
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Em forma de bloqueio do eixo hormonal, dependência vitalícia, efeitos colaterais e frustração.
              </p>
              
              <p className="text-xl text-[#2A2A2A] mt-8" style={{ fontFamily: 'var(--font-heading)' }}>
                Hormônios mudam vidas — para melhor ou para pior.
              </p>
              
              <p className="text-[#B8944D] font-medium">
                A diferença está em como, quando e com quem esse tratamento é feito.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Minha história com reposição hormonal */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Eu vivo na pele o que eu trato nos meus pacientes.
            </h2>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                Após sofrer um bloqueio completo do eixo hormonal por um tratamento mal conduzido, 
                passei a depender de reposição hormonal contínua pelo resto da vida.
              </p>
              
              <p>
                Isso mudou completamente minha forma de enxergar esse tipo de terapia.
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Foi a partir dessa experiência que construí minha filosofia médica:
              </p>
              
              <blockquote className="border-l-4 border-[#B8944D] pl-6 py-2 text-xl text-[#2A2A2A]" style={{ fontFamily: 'var(--font-heading)' }}>
                Hormônios não devem ser estimulados precocemente, nem repostos de forma desnecessária ou irresponsável.
              </blockquote>
              
              <p>
                Ao invés de aceitar a situação, mergulhei nos estudos em reposição hormonal, 
                emagrecimento clínico e terapias endovenosas.
              </p>
              
              <p>
                Hoje, conduzo meus pacientes com o mesmo critério, cuidado e responsabilidade 
                que eu gostaria de ter recebido.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Qual é o objetivo da reposição */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="section-title mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O objetivo não é excesso.
            </h2>
            <p className="text-xl text-[#B8944D] mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              É equilíbrio.
            </p>
            
            <div className="text-lg text-[#666666] leading-relaxed space-y-6 text-left">
              <p>
                O objetivo do meu acompanhamento não é ultrapassar limites fisiológicos, 
                nem buscar valores suprafisiológicos.
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Trabalhamos sempre para manter os hormônios dentro da faixa fisiológica.
              </p>
              
              <p>
                Nos valores mais altos dessa faixa, que são justamente aqueles associados a melhores 
                resultados clínicos, funcionais e de bem-estar.
              </p>
              
              <p>
                Tudo isso com segurança, controle e individualização total.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Para quem é / Para quem não é */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 
              className="section-title"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Este tratamento é para você?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Para quem é */}
            <div className="card-premium bg-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#B8944D]/10 flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#B8944D]" />
                </div>
                <h3 
                  className="text-xl text-[#2A2A2A]"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Para quem é
                </h3>
              </div>
              
              <ul className="space-y-3">
                {[
                  'Para homens e mulheres com sintomas reais de deficiência hormonal',
                  'Para quem tem exames que justificam reposição',
                  'Para quem busca qualidade de vida',
                  'Para quem valoriza acompanhamento médico contínuo',
                  'Para quem quer segurança a longo prazo',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#B8944D] flex-shrink-0 mt-0.5" />
                    <span className="text-[#666666]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Para quem não é */}
            <div className="card-premium bg-[#E5E2DD]/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#2A2A2A]/10 flex items-center justify-center">
                  <X className="w-5 h-5 text-[#2A2A2A]" />
                </div>
                <h3 
                  className="text-xl text-[#2A2A2A]"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Para quem não é
                </h3>
              </div>
              
              <ul className="space-y-3">
                {[
                  'Para quem quer hormônios por estética',
                  'Para quem quer valores suprafisiológicos',
                  'Para quem quer doses altas',
                  'Para quem não quer acompanhamento',
                  'Para quem busca atalhos',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-[#888888] flex-shrink-0 mt-0.5" />
                    <span className="text-[#888888]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Como funciona o acompanhamento */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="section-title mb-12 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Como funciona a reposição hormonal na minha clínica.
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: ClipboardList,
                  title: 'Consulta e anamnese aprofundada',
                  description: 'Entendemos sintomas, queixas físicas, emocionais, histórico clínico e funcionamento dos eixos hormonais.'
                },
                {
                  icon: TestTube,
                  title: 'Avaliação laboratorial',
                  description: 'Solicitamos exames para definir valores basais e entender como o organismo está funcionando antes de qualquer intervenção.'
                },
                {
                  icon: Stethoscope,
                  title: 'Definição da estratégia terapêutica',
                  description: 'Com base na história clínica e nos exames, definimos se o caso é de reposição hormonal ou reativação do eixo hormonal.'
                },
                {
                  icon: Heart,
                  title: 'Plano individualizado',
                  description: 'Cada paciente recebe um plano específico para sua realidade.'
                },
                {
                  icon: Activity,
                  title: 'Acompanhamento contínuo',
                  description: 'Reavaliações periódicas, ajustes de dose e prevenção de efeitos colaterais.'
                },
              ].map((item, index) => (
                <div key={index} className="card-premium">
                  <div className="w-12 h-12 rounded-full bg-[#B8944D]/10 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#B8944D]" />
                  </div>
                  <h3 
                    className="text-lg text-[#2A2A2A] mb-2"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#666666] leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dose, Segurança e Acompanhamento */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Não existe dose padrão.
            </h2>
            <p className="text-xl text-[#B8944D] mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              Existe dose certa para você.
            </p>
            
            <div className="space-y-6 text-lg text-[#666666] leading-relaxed">
              <p>
                Nosso princípio é sempre o mesmo:
              </p>
              
              <p className="text-[#2A2A2A] font-medium">
                Usar a menor dose possível que mantenha o paciente bem, estável e seguro.
              </p>
              
              <p>
                Quando encontramos essa dose ideal, entramos em uma fase de tratamento contínuo. 
                Que, na maioria dos casos, acompanha o paciente ao longo da vida.
              </p>
              
              <p>Isso exige:</p>
              
              <ul className="space-y-2 ml-4">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#B8944D]" />
                  <span>Exames periódicos</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#B8944D]" />
                  <span>Reavaliações clínicas</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#B8944D]" />
                  <span>Ajustes finos ao longo do tempo</span>
                </li>
              </ul>
              
              <p className="text-[#2A2A2A] font-medium mt-6">
                Reposição hormonal não é estática. É um processo médico contínuo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* O grande diferencial */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O diferencial não é prescrever hormônio.
            </h2>
            <p className="text-xl text-[#B8944D] mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              É saber quando não prescrever.
            </p>
            
            <ul className="space-y-4">
              {[
                'Médico que vive reposição hormonal na própria vida',
                'Abordagem anti-excesso',
                'Faixa fisiológica alta, nunca suprafisiológica',
                'Exames e reavaliações constantes',
                'Comunicação direta com o médico',
                'Foco em segurança a longo prazo',
                'Tratamento ético e responsável',
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#B8944D]" />
                  <span className="text-[#2A2A2A]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Dúvidas frequentes */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-12 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              As dúvidas mais comuns.
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white border border-[#E5E2DD] rounded-md px-6">
                <AccordionTrigger className="text-lg text-[#2A2A2A] hover:text-[#B8944D] hover:no-underline" style={{ fontFamily: 'var(--font-heading)' }}>
                  &quot;Vou precisar usar hormônios para sempre?&quot;
                </AccordionTrigger>
                <AccordionContent className="text-[#666666] leading-relaxed pb-6">
                  Depende do seu caso. Em alguns pacientes é possível reativar o eixo hormonal. 
                  Em outros, a reposição contínua é necessária. Tudo é definido com base em exames 
                  e resposta individual.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="bg-white border border-[#E5E2DD] rounded-md px-6">
                <AccordionTrigger className="text-lg text-[#2A2A2A] hover:text-[#B8944D] hover:no-underline" style={{ fontFamily: 'var(--font-heading)' }}>
                  &quot;Existe risco?&quot;
                </AccordionTrigger>
                <AccordionContent className="text-[#666666] leading-relaxed pb-6">
                  Todo tratamento médico tem riscos. A diferença é que aqui eles são controlados, 
                  monitorados e minimizados com acompanhamento contínuo.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="bg-white border border-[#E5E2DD] rounded-md px-6">
                <AccordionTrigger className="text-lg text-[#2A2A2A] hover:text-[#B8944D] hover:no-underline" style={{ fontFamily: 'var(--font-heading)' }}>
                  &quot;Isso é seguro?&quot;
                </AccordionTrigger>
                <AccordionContent className="text-[#666666] leading-relaxed pb-6">
                  Quando bem indicado e bem conduzido, sim. Quando feito sem critério, não.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-[#2A2A2A] text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl text-white mb-6 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Se você quer equilibrar seus hormônios com segurança e critério,{' '}
              <span className="text-[#B8944D]">vamos conversar</span>.
            </h2>
            
            <p className="text-lg text-white/70 mb-10">
              Agende uma avaliação médica e entenda se a reposição hormonal faz sentido para o seu caso.
            </p>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hormonal-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Agendar avaliação médica
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
